import React from 'react';
import { Card, CardContent, Typography, Button, Box, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';

function SeatDetails({ selectedElement, selectedData, onClose }) {
    const { t } = useTranslation();

    if (!selectedElement) return null;

    const InfoRow = ({ label, value }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                {label}:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right', ml: 2 }}>
                {value}
            </Typography>
        </Box>
    );

    const showableNotes = (selectedData?.booking?.note || []).filter(
        (note) =>
            selectedData?.ticketTypeCustomFields?.find((field) => field.name === note.label)
                ?.showInCatering,
    );

    return (
        <Card
            sx={{
                minWidth: 300,
                maxWidth: 400,
                overflow: 'auto',
                boxShadow: 3,
            }}
        >
            <CardContent sx={{ pb: 2 }}>
                {selectedData ? (
                    <>
                        <Typography variant="h6" gutterBottom>
                            {`${selectedData.booking.firstName} ${selectedData.booking.lastName}`}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 2 }}
                        >
                            {t('bookings.bookingDetails', { bookingId: selectedData.id })}
                        </Typography>

                        {selectedData.checkinNote && (
                            <>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    {t('bookingitems.columns.checkinNote')}:
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
                                    {selectedData.checkinNote}
                                </Typography>
                            </>
                        )}

                        <Divider sx={{ my: 1 }} />

                        <InfoRow
                            label={t('bookings.columns.email')}
                            value={selectedData.booking.email}
                        />
                        <InfoRow
                            label={t('bookings.columns.phone')}
                            value={selectedData.booking.phone}
                        />

                        <Divider sx={{ my: 1 }} />

                        <InfoRow
                            label={t('bookingitems.columns.ticketSeatName')}
                            value={selectedData.ticketSeatName}
                        />
                        <InfoRow
                            label={t('bookingitems.columns.ticketTypeName')}
                            value={selectedData.ticketTypeName}
                        />
                        <InfoRow
                            label={t('bookingitems.columns.ticketOptionName')}
                            value={selectedData.ticketOptionName}
                        />
                        <InfoRow
                            label={t('bookingitems.columns.price')}
                            value={`${formatCentsToUsd(selectedData.price)}`}
                        />
                        {selectedData.note && (
                            <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    {t('bookingitems.columns.note')}:
                                </Typography>
                                <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1 }}>
                                    {selectedData.note}
                                </Typography>
                            </>
                        )}
                        {showableNotes.length > 0 && (
                            <>
                                <Divider sx={{ my: 1 }} />
                                {showableNotes.map((note) => (
                                    <Box
                                        key={note.label}
                                        sx={{
                                            mb: 1,
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mb: 0.5 }}
                                        >
                                            {note.label}:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontStyle: 'italic', mb: 1 }}
                                        >
                                            {note.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </>
                        )}
                        <Button fullWidth variant="outlined" onClick={onClose} sx={{ mt: 2 }}>
                            {t('common.close')}
                        </Button>
                    </>
                ) : (
                    <>
                        <Typography variant="h6" gutterBottom>
                            {t('bookings.bookingDetailsNobooking')}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {`${t('bookingitems.noBookingForThisSeat')}: ${selectedElement.name}`}
                        </Typography>
                        <Button fullWidth variant="outlined" onClick={onClose}>
                            {t('common.close')}
                        </Button>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

export default SeatDetails;
